import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { sendEmail } from '../services/emailService';

const StorhandlaPage = () => {
  const { t } = useLanguage();
  const formRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    type: 'restaurateur',
    want: 'order',
    products: [],
    email: '',
    day: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        products: checked
          ? [...prev.products, value]
          : prev.products.filter(p => p !== value)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Übersetzte Labels für E-Mail
      const businessTypeLabels = {
        restaurateur: t('wholesalePage.restaurateur'),
        largeKitchen: t('wholesalePage.largeKitchen'),
        privateCustomer: t('wholesalePage.privateCustomer')
      };
      
      const wantLabels = {
        order: t('wholesalePage.order'),
        quote: t('wholesalePage.quote')
      };
      
      const productLabels = {
        carrots: t('wholesalePage.carrots'),
        potatoes: t('wholesalePage.potatoes'),
        goodies: t('wholesalePage.goodies'),
        spruceTipSyrup: t('wholesalePage.spruceTipSyrup'),
        grillSauce: t('wholesalePage.grillSauce'),
        other: t('wholesalePage.other')
      };

      await sendEmail({
        formType: 'wholesale',
        businessType: formData.type,
        businessTypeLabel: businessTypeLabels[formData.type],
        wantLabel: wantLabels[formData.want],
        productsLabels: formData.products.map(p => productLabels[p]),
        ...formData
      });

      setIsSubmitted(true);
      setFormData({
        name: '',
        type: 'restaurateur',
        want: 'order',
        products: [],
        email: '',
        day: '',
        message: ''
      });

      setTimeout(() => {
        setIsSubmitted(false);
      }, 5000);
    } catch (err) {
      console.error('Error sending email:', err);
      setError(err.message || 'Fehler beim Versenden der Nachricht');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="container mx-auto px-4 max-w-3xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-lg shadow-xl overflow-hidden"
        >
          <div className="bg-primary-green p-8 text-center">
            <h1 className="text-3xl font-bold text-white mb-2">
              {t('wholesalePage.title')}
            </h1>
          </div>

          <div className="p-8 md:p-12">
            {isSubmitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl text-green-600">✓</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-2">
                  {t('wholesalePage.thankYou')}
                </h3>
                <button 
                  onClick={() => setIsSubmitted(false)}
                  className="mt-6 text-primary-green hover:underline"
                >
                  Skicka ett nytt meddelande
                </button>
              </div>
            ) : (
              <>
                {error && (
                  <div className="mb-6 p-4 bg-red-100 border border-red-400 rounded-lg text-red-700">
                    {error}
                  </div>
                )}

                <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    {t('wholesalePage.myNameIs')}
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder={t('wholesalePage.yourName')}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-accent-green focus:ring-2 focus:ring-accent-green/20 outline-none transition-all"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    {t('wholesalePage.iAm')}
                  </label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-accent-green focus:ring-2 focus:ring-accent-green/20 outline-none transition-all bg-white"
                  >
                    <option value="restaurateur">{t('wholesalePage.restaurateur')}</option>
                    <option value="largeKitchen">{t('wholesalePage.largeKitchen')}</option>
                    <option value="privateCustomer">{t('wholesalePage.privateCustomer')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    {t('wholesalePage.iWant')}
                  </label>
                  <select
                    name="want"
                    value={formData.want}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-accent-green focus:ring-2 focus:ring-accent-green/20 outline-none transition-all bg-white"
                  >
                    <option value="order">{t('wholesalePage.order')}</option>
                    <option value="quote">{t('wholesalePage.quote')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    {t('wholesalePage.regarding')}
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <label className="flex items-center space-x-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <input 
                        type="checkbox" 
                        value="carrots"
                        checked={formData.products.includes('carrots')}
                        onChange={handleInputChange}
                        className="text-accent-green focus:ring-accent-green" 
                      />
                      <span>{t('wholesalePage.carrots')}</span>
                    </label>
                    <label className="flex items-center space-x-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <input 
                        type="checkbox" 
                        value="potatoes"
                        checked={formData.products.includes('potatoes')}
                        onChange={handleInputChange}
                        className="text-accent-green focus:ring-accent-green" 
                      />
                      <span>{t('wholesalePage.potatoes')}</span>
                    </label>
                    <label className="flex items-center space-x-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <input 
                        type="checkbox" 
                        value="goodies"
                        checked={formData.products.includes('goodies')}
                        onChange={handleInputChange}
                        className="text-accent-green focus:ring-accent-green" 
                      />
                      <span>{t('wholesalePage.goodies')}</span>
                    </label>
                    <label className="flex items-center space-x-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <input 
                        type="checkbox" 
                        value="spruceTipSyrup"
                        checked={formData.products.includes('spruceTipSyrup')}
                        onChange={handleInputChange}
                        className="text-accent-green focus:ring-accent-green" 
                      />
                      <span>{t('wholesalePage.spruceTipSyrup')}</span>
                    </label>
                    <label className="flex items-center space-x-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <input 
                        type="checkbox" 
                        value="grillSauce"
                        checked={formData.products.includes('grillSauce')}
                        onChange={handleInputChange}
                        className="text-accent-green focus:ring-accent-green" 
                      />
                      <span>{t('wholesalePage.grillSauce')}</span>
                    </label>
                    <label className="flex items-center space-x-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <input 
                        type="checkbox" 
                        value="other"
                        checked={formData.products.includes('other')}
                        onChange={handleInputChange}
                        className="text-accent-green focus:ring-accent-green" 
                      />
                      <span>{t('wholesalePage.other')}</span>
                    </label>
                  </div>
                  <p className="text-sm text-gray-500 mt-2 italic">
                    {t('wholesalePage.quantityNote')}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-gray-700 font-semibold mb-2">
                      {t('wholesalePage.emailAddress')}
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="email@example.com"
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-accent-green focus:ring-2 focus:ring-accent-green/20 outline-none transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 font-semibold mb-2">
                      {t('wholesalePage.day')}
                    </label>
                    <input
                      type="date"
                      name="day"
                      value={formData.day}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-accent-green focus:ring-2 focus:ring-accent-green/20 outline-none transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    {t('wholesalePage.message')}
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows="4"
                    placeholder={t('wholesalePage.messagePlaceholder')}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-accent-green focus:ring-2 focus:ring-accent-green/20 outline-none transition-all resize-none"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full py-4 rounded-lg font-bold text-white text-lg transition-all duration-300 ${
                    isSubmitting ? 'bg-gray-400 cursor-not-allowed' : 'btn-custom-primary shadow-lg hover:shadow-xl'
                  }`}
                >
                  {isSubmitting ? '...' : t('wholesalePage.submit')}
                </button>
              </form>
              </>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default StorhandlaPage;